import { bootstrapApplication } from '@angular/platform-browser';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import { MessageService } from 'primeng/api';
import { provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { Chart, registerables } from 'chart.js';
import Aura from '@primeng/themes/aura';
import { environment } from './environments/environment';
import { redirectToCanonicalHost } from '@app/shared/utils/canonical-host';
import { AuthUserEffects } from './app/store/store-auth-user/auth-user.effects';
import { ProductCatalogEffects } from './app/store/catalog/product-catalog.effects';
import { ProductBasketEffects } from './app/store/product-basket/product-basket.effects';
import { HeadProductDetailsEffects } from './app/store/head-product-details/head-product-details.effects';
import { ShoppingListEffects } from '@store/shopping-list/shopping-list.effects';
import { withCredentialsInterceptor } from './app/core/interceptors/with-credentials.interceptor';
import {
    API_BASE_URL,
    AuthClient,
    HeadProductClient,
    ProductBasketClient,
    ProductCatalogClient,
    ProductClient,
    ShoppingListClient,
    UserProductCatalogClient,
} from './api-service';
import { AppComponent } from './app/app.component';
import { routes } from './app/app.routes';
import { authUserReducer } from './app/store/store-auth-user/auth-user.reducer';
import { productCatalogReducer } from './app/store/catalog/product-catalog.reducer';
import { productBasketReducer } from './app/store/product-basket/product-basket.reducer';
import { loadingReducer } from './app/store/store-loading/loading.reducer';
import { headProductDetailsReducer } from './app/store/head-product-details/head-product-details.reducer';
import { shoppingListReducer } from './app/store/shopping-list/shopping-list.reducer';

// The App Service default *.azurewebsites.net hostname keeps serving this app after the
// custom domain is bound, and cookie auth silently fails there (see canonical-host.ts).
// If we are on it the browser is already navigating away - do not bootstrap or call the API.
if (!redirectToCanonicalHost()) {
    Chart.register(...registerables);

    bootstrapApplication(AppComponent, {
        providers: [
            provideRouter(routes),
            provideAnimationsAsync(),
            provideHttpClient(withInterceptors([withCredentialsInterceptor]), withInterceptorsFromDi()),
            provideTranslateService({
                loader: provideTranslateHttpLoader({
                    prefix: './assets/i18n/',
                    suffix: '.json',
                }),
            }),
            MessageService,
            ProductCatalogClient,
            HeadProductClient,
            UserProductCatalogClient,
            AuthClient,
            ProductBasketClient,
            ShoppingListClient,
            ProductClient,
            { provide: API_BASE_URL, useValue: environment.apiUrl },
            provideStore({
                authUser: authUserReducer,
                productCatalog: productCatalogReducer,
                productBasket: productBasketReducer,
                loading: loadingReducer,
                headProductDetails: headProductDetailsReducer,
                shoppingList: shoppingListReducer,
            }),
            provideEffects([AuthUserEffects, ProductCatalogEffects, ProductBasketEffects, HeadProductDetailsEffects, ShoppingListEffects]),

            ...(!environment.production
                ? [
                      provideStoreDevtools({
                          maxAge: 25,
                          logOnly: false,
                      }),
                  ]
                : []),

            providePrimeNG({
                theme: {
                    preset: Aura,
                    options: {
                        darkModeSelector: '.dark',
                        cssLayer: {
                            name: 'primeng',
                            order: 'primeflex, primeng, app-styles',
                        },
                    },
                },
                ripple: true,
            }),
        ],
    }).catch(err => console.error(err));
}
